import {
  Autocomplete,
  FormControl,
  TextField,
  type AutocompleteRenderInputParams,
} from '@mui/material';
import type { GcpSubnet } from '@repo/api-gw-sdk';
import { useMemo } from 'react';

import { useDAL } from '@/contexts/useDAL';

import { EmptySelect } from './emptySelect';

interface OwnedSubnetsSelectProps {
  accountId: string;
  subnetName: string;
  region: string;
  networkName?: string;
  onChange: (
    subnetName?: string,
    isUserChange?: boolean,
    networkHostProject?: string
  ) => void;
  isSharedNetwork?: boolean;
}

export const GcpSubnetSelect = (props: OwnedSubnetsSelectProps) => {
  if (!props.networkName || !props.region || !props.accountId) {
    return <EmptySelect title='' />;
  }

  return props.isSharedNetwork ? (
    <SharedSubnetsSelect {...props} />
  ) : (
    <OwnedSubnetsSelect {...props} />
  );
};

const OwnedSubnetsSelect = (props: OwnedSubnetsSelectProps) => {
  const dal = useDAL();
  const { data: body } = dal.restore.gcp.listSubnets(
    props.accountId,
    props.region,
    props.networkName
  );
  return <InnerGcpSubnetSelect subnets={body?.subnets ?? []} {...props} />;
};

const SharedSubnetsSelect = (props: OwnedSubnetsSelectProps) => {
  const dal = useDAL();
  const { data: body } = dal.restore.gcp.listSharedSubnets(
    props.accountId,
    props.region,
    props.networkName
  );
  return <InnerGcpSubnetSelect subnets={body?.subnets ?? []} {...props} />;
};

const InnerGcpSubnetSelect = ({
  subnetName,
  networkName,
  onChange,
  subnets,
}: OwnedSubnetsSelectProps & { subnets: GcpSubnet[] }) => {
  const filteredSubnets = useMemo(() => {
    return subnets
      .filter((subnet) =>
        networkName ? subnet.vpcNetwork === networkName : true
      )
      .map((subnet) => ({
        label: subnet.name,
        project: subnet.project,
      }));
  }, [subnets, networkName]);

  return (
    <FormControl size='small' className='flex-1'>
      <Autocomplete
        sx={{
          '& .MuiInputBase-root': {
            height: 30,
            minHeight: 30,
          },
        }}
        clearOnBlur
        data-testid='gcpSubnetSelectByName'
        value={filteredSubnets.find((s) => s.label === subnetName) || null}
        onChange={(event, newValue) =>
          onChange(newValue?.label || undefined, !!event, newValue?.project)
        }
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.label === value.label}
        renderInput={(params: AutocompleteRenderInputParams) => {
          return (
            <TextField
              {...params}
              sx={{
                '& input::placeholder': {
                  color: 'var(--mui-palette-text-primary)',
                  opacity: 1,
                },
              }}
              placeholder='Select'
              label=''
              InputLabelProps={{ shrink: true }}
            />
          );
        }}
        options={filteredSubnets}
      />
    </FormControl>
  );
};
